import { coercePref } from './coerce-pref.util';

export type NumberFormatPref = 'system' | 'us' | 'space' | 'plain' | 'plain-comma';

export const NUMBER_FORMAT_KEY = 'display.numberFormat';

export interface Separators {
  group: string;
  decimal: string;
}

const NBSP = ' ';
// A digit-anchored numeric run: a span of digits/separators that ends in a digit. Used to
// confine the separator swap to the number itself, so a '.'/',' living in a currency symbol
// or code (e.g. 'Kr.', 'BRL') is never touched.
//
// LIMIT: the run is delimited by any non-[\d.,] character, so this holds only while a
// dot-carrying symbol is SEPARATED from the digits by such a character — Angular's CurrencyPipe
// emits 'Kr. 1.234,56' (the space breaks the run) and 'Kr.1.234,56' would not be protected (the
// run would start at the symbol's dot). No locale/currency Angular renders produces the glued
// form, and no in-tree call site passes a custom dot-suffixed symbol; the applySeparators specs
// pin both shapes so a future one can't regress silently.
const NUMERIC_RUN = /[\d.,]*\d/g;
// The two swappable glyphs inside a numeric run. Hoisted (like NUMERIC_RUN) so applySeparators —
// called for every cell of a large table on each cache miss — doesn't re-allocate it per run.
const RUN_GLYPHS = /[.,]/g;

// Single source of truth for the preset set: the NumberFormatPref union, separatorsFor, and
// coerceNumberFormat all derive from this map. Adding a preset here is the only edit needed —
// no parallel switch/`||` chain to keep in lockstep (a missed one would silently coerce the
// new preset back to 'system').
const SEPARATORS: Record<NumberFormatPref, Separators> = {
  system: { group: '.', decimal: ',' },
  us: { group: ',', decimal: '.' },
  space: { group: NBSP, decimal: ',' },
  plain: { group: '', decimal: '.' },
  'plain-comma': { group: '', decimal: ',' },
};

export function separatorsFor(pref: NumberFormatPref): Separators {
  return SEPARATORS[pref] ?? SEPARATORS.system;
}

export function coerceNumberFormat(v: unknown): NumberFormatPref {
  // Derive the valid set from SEPARATORS (own keys only) so the preset list stays single-sourced,
  // and delegate the string-guard/whitelist to the shared coercer used by every display.* pref.
  return coercePref(v, Object.keys(SEPARATORS) as NumberFormatPref[], 'system');
}

/** Swap an Angular it-locale string ('.' groups, ',' decimal) onto the target separators.
 *  Identity when `sep` is the system pair. The swap is confined to each digit-anchored
 *  numeric run (`NUMERIC_RUN`), so a '.'/',' that lives in a currency symbol or code — e.g.
 *  `appCurrency:'EUR':'Kr.'` rendering 'Kr. 1.234,56', or a letter code like 'BRL' — is left
 *  untouched. Within a run the two glyphs can't cross-contaminate: '.' always maps to the
 *  group separator and ',' to the decimal, in one pass. */
export function applySeparators(angularItString: string, sep: Separators): string {
  // Fast path for the default 'system' preset (the it-locale base itself): most users, and
  // every cell of a large table re-formats on each cache miss, so skip the scan when the
  // output would be byte-identical to the input.
  if (sep.group === '.' && sep.decimal === ',') return angularItString;
  return angularItString.replace(NUMERIC_RUN, run =>
    run.replace(RUN_GLYPHS, ch => (ch === '.' ? sep.group : sep.decimal)),
  );
}

/** Lenient parse for the input directive. Strips grouping/whitespace and resolves the
 *  decimal point. When `sep` (the user's active separators) is given, a single separator
 *  is classified as decimal-or-grouping by matching the user's preference — so a 3+ digit
 *  fraction like '12,505' is read as 12.505, not 12505. Otherwise only a genuine 3-digit
 *  tail is read as grouping; any other tail length is the decimal point (so '1.2500' is 1.25,
 *  not 12500). Malformed grouping ('1,,5', '1.2.3') is rejected as null rather than silently
 *  concatenated. Integer mode truncates.
 *
 *  ACCEPTED AMBIGUITY (reviewed 2026-07-09, kept as-is): under a punctuation-grouping preset —
 *  'system' (the default) and 'us' — a single GROUP glyph with an exactly-3-digit tail is read
 *  as thousands grouping, so under 'system' '1.500' -> 1500 and '19.995' -> 19995, silently.
 *  This is the correct it-locale reading (the user's decimal glyph under 'system' is ',', so
 *  '1,5' -> 1.5 works), but a user in the habit of typing '.' as a decimal separator can commit a
 *  x1000 value with no error. The shape is irreducibly ambiguous ('1.500' = 1500 vs 1.5, no way
 *  to tell), so the two alternatives — reject-as-invalid, or always-treat-a-single-group-glyph-as-
 *  decimal — each break a legitimate user, and we deliberately keep the locale-correct grouping
 *  reading rather than trade one silent-wrong for another. Revisit only if usage shows the
 *  '.'-decimal habit dominates. A '0'-led integer part ('0.500') is EXEMPT — it has no legitimate
 *  grouping reading at all, so it is read as the decimal (see resolveSingleSeparator rule 3).
 *
 *  The `sep` parameter is optional ONLY for the unit tests, which exercise a legacy blanket-lenient
 *  mode (whitespace-strip + rightmost-is-decimal heuristic) with no preset; every PRODUCTION caller
 *  (AppNumberInputDirective) passes the active separators. That no-`sep` mode is a deliberately
 *  retained test-facing contract, not dead code — see the specs — so the branch stays. */
export function parseNumber(
  raw: string,
  mode: 'integer' | 'decimal',
  sep?: Separators,
): number | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === '') return null;
  const negative = s.startsWith('-');
  // Strip a leading sign. type=number accepted an explicit '+5'; keep parity so a typed or
  // pasted leading '+' yields 5, not null (the char-whitelist below would otherwise reject it).
  if (negative || s.startsWith('+')) s = s.slice(1);
  // Only strip whitespace when it is meaningful grouping — i.e. the 'space' preset (NBSP group),
  // or a direct unit-test call with no `sep` (legacy lenient behaviour). Under any other preset
  // whitespace has no meaning, so a stray/typo'd space must NOT be absorbed as grouping ('1 5'
  // under 'us' would otherwise become 15): leave it in so the char-whitelist below rejects it
  // as invalid (null) rather than committing a silently-wrong number.
  if (sep && /\s/.test(sep.group)) {
    // 'space' preset (NBSP grouping): validate the whitespace grouping structure BEFORE stripping,
    // exactly as stripGrouping validates '.'/','-grouping. Without this a fat-finger like '1 5' (or
    // '12 34 5') gets blanket-stripped to '15' / '12345' and silently commits a wrong number, while
    // the same malformed shape under a '.'/',' preset ('1.2.3') is correctly rejected as null. Only
    // the integer part groups; a trailing decimal part (after the first '.'/',') keeps its
    // whitespace so the char-whitelist below rejects a stray space inside it as invalid.
    const decIdx = s.search(/[.,]/);
    const intPart = decIdx === -1 ? s : s.slice(0, decIdx);
    const rest = decIdx === -1 ? '' : s.slice(decIdx);
    if (/\s/.test(intPart)) {
      const groups = intPart.split(/\s+/);
      if (!/^\d{1,3}$/.test(groups[0])) return null;
      for (let i = 1; i < groups.length; i++) {
        if (!/^\d{3}$/.test(groups[i])) return null;
      }
    }
    s = intPart.replace(/\s/g, '') + rest;
  } else if (!sep) {
    // Legacy lenient behaviour for a direct unit-test call with no `sep`: strip all whitespace.
    s = s.replace(/\s/g, '');
  }
  if (s === '') return null;
  // Reject anything that isn't a digit or a separator instead of silently deleting it: the
  // old blanket strip turned '1e999' into '1999' and '1,5x' into '1,5', committing a
  // plausible-but-wrong number with no validation error. type=number used to block these at
  // entry; on type=text we surface the bad input as null (empty/invalid) rather than corrupt it.
  if (/[^0-9.,]/.test(s)) return null;

  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  let canonical: string | null;
  if (hasDot && hasComma) {
    // A preset that doesn't group with a '.'/',' glyph — 'plain'/'plain-comma' (group '') and
    // 'space' (group NBSP) — has no '.'/','-based thousands separator, so a value carrying BOTH
    // glyphs is structurally impossible under it. Reject it rather than apply the rightmost-is-
    // decimal heuristic below — which would silently accept e.g. '1.234,5' (pasted under 'plain'
    // or 'space') as 1234.5. Mirrors resolveSingleSeparator's punctuation-grouping guard, keeping
    // the two branches consistent for every non-'.'/',' grouping preset.
    if (sep && !groupsWithPunctuation(sep)) return null;
    // Both glyphs present: the rightmost one is the decimal point, the other groups. Validate
    // the integer part's grouping (stripGrouping returns null for a malformed run like '1.2,3')
    // so a typo surfaces as invalid rather than a silently concatenated number.
    const decIdx = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
    const groupCh: '.' | ',' = s[decIdx] === '.' ? ',' : '.';
    const intPart = stripGrouping(s.slice(0, decIdx), groupCh);
    if (intPart == null) return null;
    canonical = `${intPart}.${s.slice(decIdx + 1)}`; // nothing after the rightmost separator groups
  } else if (hasDot || hasComma) {
    canonical = resolveSingleSeparator(s, hasDot ? '.' : ',', sep);
  } else {
    canonical = s;
  }
  if (canonical == null) return null;

  let n = parseFloat(canonical);
  if (isNaN(n)) return null;
  if (mode === 'integer') n = Math.trunc(n);
  return negative ? -n : n;
}

/** True when the preset groups with a punctuation glyph ('.' or ','), i.e. 'system' or 'us'.
 *  For 'plain'/'plain-comma' (group '') and 'space' (group NBSP) it is false: their grouping
 *  glyph is not '.'/',', so any '.'/',' in the input can only be a decimal point, never thousands
 *  grouping. Both parseNumber branches gate on this so a foreign 3-digit-tail separator (e.g.
 *  '1.234' under 'space') is read as the decimal, not mis-scaled x1000. */
function groupsWithPunctuation(sep?: Separators): boolean {
  return !!sep && (sep.group === '.' || sep.group === ',');
}

/** Strip a grouping separator `ch` from an integer-part string, returning null when the
 *  grouping is malformed. Valid grouping is thousands-style: the leading group is 1-3 digits
 *  and does NOT start with '0', every later group is EXACTLY 3, and every group is ALL DIGITS
 *  ('1.234.567' -> '1234567'). A malformed run like '1,,5' (empty group), '1.2.3' (1-/1-digit
 *  groups), '0.123.456' (leading-zero group) or '1,2.345' (a group carrying the other,
 *  decimal-kind glyph — reachable from the both-separators branch, where slicing off the
 *  rightmost separator can leave a stray one behind) returns null so parseNumber surfaces it as
 *  invalid instead of silently concatenating it. A single occurrence (no grouping) passes through
 *  only when it is itself all digits — a bare '0' or '007' integer part is fine, it just isn't
 *  grouped. */
function stripGrouping(intPart: string, ch: '.' | ','): string | null {
  const parts = intPart.split(ch);
  // A length-only check would accept a group that still holds the OTHER separator (e.g. the
  // '1,2' in stripGrouping('1,2.345', '.')): length 3, but '1,2' is not a real group. parseFloat
  // would then stop at that glyph and commit a wrong number, so validate digits, not just length.
  if (parts.length === 1) return /^\d+$/.test(parts[0]) ? parts[0] : null;
  // The leading group must not start with '0': no correctly-grouped number is written '0.123.456'
  // or '01.234'. Without this, `stripGrouping('0.123', '.')` returned '0123' -> 123, silently
  // scaling a value the user meant as a fraction (see resolveSingleSeparator's leading-zero rule,
  // which catches the single-separator shape before it ever reaches here). This branch guards the
  // remaining callers: a repeated separator, and the both-separators integer part ('0.123,45').
  if (!/^[1-9]\d{0,2}$/.test(parts[0])) return null;
  for (let i = 1; i < parts.length; i++) {
    if (!/^\d{3}$/.test(parts[i])) return null;
  }
  return parts.join('');
}

/** Resolve a string carrying a single kind of separator `ch` to its canonical dot-decimal
 *  form, or null if it is malformed. Precedence:
 *   1. The user's own decimal glyph (single occurrence) is unambiguously the decimal point,
 *      regardless of tail length — '12,505' under the system preset is 12.505, not 12505.
 *   2. A single leading separator ('.234') has an empty integer part, so it can only be a
 *      decimal point ('.234' -> 0.234), regardless of tail length — grouping never leads. This
 *      also matches what the native type=number input it replaced accepted, independent of preset.
 *   3. A single separator whose integer part starts with '0' ('0.125', '00.125') is a decimal
 *      point too: a leading-zero group is not valid thousands grouping in any locale, so unlike
 *      rule 5's '1.500' the shape is NOT ambiguous, and reading it as grouping would silently
 *      scale the value x1000 ('0.500' -> 500 in a price field).
 *   4. A single separator whose tail is NOT 3 digits cannot be a thousands group, so it is the
 *      decimal point: '1.25' -> 1.25, '1.2500' -> 1.25 (previously mis-scaled to 12500). A
 *      trailing separator ('1.') is a decimal point too.
 *   5. Otherwise treat `ch` as grouping and validate the structure (stripGrouping): a genuine
 *      3-digit tail groups ('1.250' -> 1250, matching the it-locale reading), a malformed run
 *      is rejected as null. The 3-digit-tail case is the one irreducibly ambiguous shape
 *      (a thousands group vs a 3-place fraction); it resolves to grouping. */
function resolveSingleSeparator(s: string, ch: '.' | ',', sep?: Separators): string | null {
  const count = s.split(ch).length - 1;
  // Presets that don't group with a '.'/',' glyph — 'plain'/'plain-comma' (group '') and 'space'
  // (group NBSP; its whitespace grouping is already stripped upstream): grouping with `ch` is
  // impossible, so `ch` can ONLY be a decimal point — even the foreign glyph (e.g. a '.' typed/
  // pasted under 'plain-comma' or 'space', whose decimal is ','). A single occurrence is that
  // decimal; more than one is two decimal points = malformed. Without this, the tail-length rule
  // below would read a foreign 3-digit-tail separator as thousands grouping and silently scale
  // the value x1000 ('1.234' -> 1234 instead of 1.234 under a preset with no '.'/',' grouping).
  if (sep && !groupsWithPunctuation(sep)) return count === 1 ? s.replace(ch, '.') : null;
  if (sep && ch === sep.decimal && count === 1) return s.replace(ch, '.'); // the user's decimal glyph
  if (count === 1 && s.startsWith(ch)) return s.replace(ch, '.'); // leading separator: '.234' -> 0.234
  // A '0'-led integer part can never be a thousands group — no locale writes 1500 as '0.125' or
  // '01.234'. Only the 3-digit-tail shape survives to the grouping branch below (rule 4 already
  // resolved every other tail length), and for that shape the grouping reading silently scales the
  // value x1000: '0.500' typed into a price field committed 500. Read `ch` as the decimal point
  // instead. This is NOT the irreducible '1.500' ambiguity documented on parseNumber — that shape
  // has a legitimate it-locale grouping reading, this one has none.
  if (count === 1 && s.startsWith('0')) return s.replace(ch, '.'); // '0.125' -> 0.125, never 125

  const tail = s.length - s.lastIndexOf(ch) - 1;
  if (count === 1 && tail !== 3) return s.replace(ch, '.');

  return stripGrouping(s, ch); // 3-digit tail or repeated -> grouping (validated)
}

/** True when `String(n)` renders `n` in exponential form ('1e+21', '5e-7'). JS switches to
 *  exponential for magnitudes >= 1e21 or < 1e-6; in that form the decimal-shift string used by
 *  roundToDecimals is malformed and the plain-string reparse is wrong, so both callers must fall
 *  back. Single-sourced here so the "when does String(n) go exponential" rule can't drift between
 *  roundToDecimals and AppNumberInputDirective.plainString. */
export function isExponentialForm(n: number): boolean {
  return /e/i.test(String(n));
}

/** Round `n` to `d` fraction digits, half away from zero, WITHOUT toFixed's binary-float bias.
 *  `(1.005).toFixed(2)` is "1.00" because 1.005 is stored as 1.00499…; shifting the decimal
 *  point via an exponent STRING re-parses the literal exactly (`'1.005e2'` → 100.5), so
 *  Math.round lands on 101 → 1.01. Falls back to toFixed for magnitudes String() renders in
 *  exponential form (where the shift string would be malformed) — far outside any money or
 *  quantity field. Shared by AppNumberInputDirective (its [maxDecimals] clamp) and any
 *  component that patches a rounded value into an appNumberInput control, so the two can't
 *  drift onto divergent (and biased) rounding. Caller guards `d` is a non-negative integer. */
export function roundToDecimals(n: number, d: number): number {
  const abs = Math.abs(n);
  if (!Number.isFinite(abs) || isExponentialForm(abs)) return parseFloat(n.toFixed(Math.min(d, 100)));
  const rounded = Number(`${Math.round(Number(`${abs}e${d}`))}e${-d}`);
  // A large `d` (or a shift that itself overflows into exponential form) makes the second
  // interpolation malformed — String(1.5e25) is '1.5e+25', so `${…}e-25` parses to NaN. Fall
  // back to toFixed rather than committing NaN. (No in-tree caller passes such a `d`, but it is
  // caller-controlled, so guard the value.)
  if (!Number.isFinite(rounded)) return parseFloat(n.toFixed(Math.min(d, 100)));
  return n < 0 ? -rounded : rounded;
}
